/**
 * InitialProposalReview Component
 *
 * Grouped section-based review UI rendered in the chat panel.
 * Shows AI-extracted KG entries grouped by domain for user acceptance/rejection.
 * Users can accept or reject fields individually, by section, or all at once.
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  CheckCheck,
  X,
  Sparkles,
  FileText,
} from 'lucide-react';
import type { KnowledgeGraphEntry } from '../../../types/complianceWorkflow';
import { ConfidenceBadge } from '../ConfidenceBadge';
import { cleanKGValue, groupEntriesByDomain } from '../../../utils/kgFieldMapping';
import { formatProposalValue } from './ExtractionProposal';

interface InitialProposalReviewProps {
  entries: KnowledgeGraphEntry[];
  clientId: string;
  onAccept: (entries: KnowledgeGraphEntry[]) => void;
  onReject: (entries: KnowledgeGraphEntry[]) => void;
}

type EntryStatus = 'pending' | 'accepted' | 'rejected';

/** Format a field_path into a human-readable label. */
function fieldLabel(fieldPath: string): string {
  const lastPart = fieldPath.split('.').pop() || fieldPath;
  return lastPart
    .replace(/(?:_|\[|\]|\d)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Truncate a display value if too long. TASK-604: JSON never renders —
 * formatProposalValue unwraps {text,tag} objects AND JSON-string values. */
function displayValue(value: unknown): string {
  const str = formatProposalValue(cleanKGValue(value));
  return str.length > 60 ? str.slice(0, 57) + '...' : str;
}

const InitialProposalReview: React.FC<InitialProposalReviewProps> = ({
  entries,
  clientId: _clientId,
  onAccept,
  onReject,
}) => {
  const [statuses, setStatuses] = useState<Record<string, EntryStatus>>({});
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const groups = useMemo(() => groupEntriesByDomain(entries), [entries]);

  const getStatus = (id: string): EntryStatus => statuses[id] || 'pending';

  const pendingCount = useMemo(
    () => entries.filter((e) => getStatus(e.id) === 'pending').length,
    [entries, statuses]
  );

  const toggleSection = useCallback((domain: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, []);

  // ── Single entry actions ──

  const acceptEntry = useCallback(
    (entry: KnowledgeGraphEntry) => {
      setStatuses((prev) => ({ ...prev, [entry.id]: 'accepted' }));
      onAccept([entry]);
    },
    [onAccept]
  );

  const rejectEntry = useCallback(
    (entry: KnowledgeGraphEntry) => {
      setStatuses((prev) => ({ ...prev, [entry.id]: 'rejected' }));
      onReject([entry]);
    },
    [onReject]
  );

  // ── Section actions ──

  const acceptSection = useCallback(
    (sectionEntries: KnowledgeGraphEntry[]) => {
      const pending = sectionEntries.filter((e) => getStatus(e.id) === 'pending');
      if (pending.length === 0) return;
      setStatuses((prev) => {
        const next = { ...prev };
        for (const e of pending) next[e.id] = 'accepted';
        return next;
      });
      onAccept(pending);
    },
    [onAccept, statuses]
  );

  const rejectSection = useCallback(
    (sectionEntries: KnowledgeGraphEntry[]) => {
      const pending = sectionEntries.filter((e) => getStatus(e.id) === 'pending');
      if (pending.length === 0) return;
      setStatuses((prev) => {
        const next = { ...prev };
        for (const e of pending) next[e.id] = 'rejected';
        return next;
      });
      onReject(pending);
    },
    [onReject, statuses]
  );

  // ── Bulk actions ──

  const acceptAll = useCallback(() => {
    const pending = entries.filter((e) => getStatus(e.id) === 'pending');
    if (pending.length === 0) return;
    setStatuses((prev) => {
      const next = { ...prev };
      for (const e of pending) next[e.id] = 'accepted';
      return next;
    });
    onAccept(pending);
  }, [entries, onAccept, statuses]);

  const rejectAll = useCallback(() => {
    const pending = entries.filter((e) => getStatus(e.id) === 'pending');
    if (pending.length === 0) return;
    setStatuses((prev) => {
      const next = { ...prev };
      for (const e of pending) next[e.id] = 'rejected';
      return next;
    });
    onReject(pending);
  }, [entries, onReject, statuses]);

  if (entries.length === 0) return null;

  return (
    <div className="border border-blue-200 rounded-lg bg-blue-50/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-blue-50 border-b border-blue-200">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-blue-900">AI Extraction Review</span>
          {pendingCount > 0 && (
            <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
              {pendingCount} pending
            </span>
          )}
        </div>
        {pendingCount > 0 && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={acceptAll}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
            >
              <CheckCheck className="w-3 h-3" />
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
            >
              <X className="w-3 h-3" />
              Reject All
            </button>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="divide-y divide-blue-100">
        {groups.map((group) => {
          const isCollapsed = collapsedSections.has(group.domain);
          const sectionPending = group.entries.filter((e) => getStatus(e.id) === 'pending').length;

          return (
            <div key={group.domain}>
              {/* Section header */}
              <div className="flex items-center justify-between px-3 py-2 bg-white/60 hover:bg-white/80 transition-colors">
                <button
                  onClick={() => toggleSection(group.domain)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-800"
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                  )}
                  {group.label}
                  <span className="text-xs text-gray-500 font-normal">
                    ({group.entries.length})
                  </span>
                </button>
                {sectionPending > 0 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => acceptSection(group.entries)}
                      className="px-1.5 py-0.5 text-xs text-green-700 hover:bg-green-50 rounded transition-colors"
                      title="Accept section"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => rejectSection(group.entries)}
                      className="px-1.5 py-0.5 text-xs text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Reject section"
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>

              {/* Entry rows */}
              {!isCollapsed && (
                <div className="divide-y divide-gray-100">
                  {group.entries.map((entry) => {
                    const status = getStatus(entry.id);
                    const isAccepted = status === 'accepted';
                    const isRejected = status === 'rejected';

                    return (
                      <div
                        key={entry.id}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                          isAccepted
                            ? 'bg-green-50/50'
                            : isRejected
                              ? 'bg-gray-50 opacity-60'
                              : 'bg-white'
                        }`}
                      >
                        {/* Field label */}
                        <span
                          className={`w-32 flex-shrink-0 text-xs font-medium truncate ${
                            isRejected ? 'line-through text-gray-400' : 'text-gray-600'
                          }`}
                          title={entry.field_path}
                        >
                          {fieldLabel(entry.field_path)}
                        </span>

                        {/* Value */}
                        <span
                          className={`flex-1 text-xs truncate ${
                            isRejected ? 'line-through text-gray-400' : 'text-gray-800'
                          }`}
                          title={formatProposalValue(cleanKGValue(entry.value))}
                        >
                          {displayValue(entry.value)}
                        </span>

                        {/* Confidence */}
                        <ConfidenceBadge confidence={entry.confidence} size="sm" />

                        {/* Source document */}
                        {entry.source_document_name && (
                          <span className="hidden sm:flex items-center gap-0.5 text-xs text-gray-400 flex-shrink-0" title={entry.source_document_name}>
                            <FileText className="w-3 h-3" />
                          </span>
                        )}

                        {/* Action buttons / Status badge */}
                        {status === 'pending' ? (
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button
                              onClick={() => acceptEntry(entry)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Accept"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => rejectEntry(entry)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Reject"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <span
                            className={`text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                              isAccepted
                                ? 'text-green-700 bg-green-100'
                                : 'text-gray-500 bg-gray-100'
                            }`}
                          >
                            {isAccepted ? 'Accepted' : 'Rejected'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InitialProposalReview;
